/**
 * The signed-in player, as reactive state — and the three syncs that come with
 * being signed in.
 *
 * `docs/DESIGN.md`'s accounts amendment says what logging in gets you:
 *
 * > personal bests sync (merge local bests up on login — keep the better
 * > result per key; server is source of truth after), win/loss record shown on
 * > a small profile card, settings (DAS/ARR, theme, etc.) synced. Guests keep
 * > localStorage exactly as today.
 *
 * That last sentence is the constraint the whole module is written against.
 * **Nothing here is on the path of a guest playing a game.** No screen waits
 * on it, no failure of it is visible anywhere except the account screen, and
 * with the network unplugged the store settles on `unavailable` and everything
 * else in the app behaves exactly as it did before accounts existed
 * (`docs/BASELINE.md` 5).
 *
 * ## The probe
 *
 * `refresh()` runs once on load. It costs one `GET /api/auth/me` which the
 * service worker treats as NetworkOnly (`docs/KNOWN-BUGS.md` item 7), so
 * offline it fails fast and locally. It is skipped entirely when the browser
 * already says it is offline — not for correctness, but because a failed
 * request in the console on every cold start of an installed PWA is noise
 * somebody will eventually chase.
 *
 * ## Why the local store is still written
 *
 * "Server is source of truth after" is about *the merge*, not about where the
 * cards read from. The Play tab reads `localStorage`, and the merged set is
 * written back down into it on login — so the same code
 * path renders a best whether or not anybody is signed in, and signing out
 * leaves a guest with everything they had.
 */
import {
  changePassword as changePasswordRequest,
  fetchProfile,
  fetchSocketTicket,
  login as loginRequest,
  logout as logoutRequest,
  pushBests,
  pushSettings,
  register as registerRequest,
} from './account-io.js';
import type { AccountBest, AccountOutcome, AccountPlayer, AccountRecord } from './account-io.js';
import { bindLocalHistory, releaseLocalHistory, syncLocalHistory } from './local-history.svelte.js';
import { loadAllPersonalBests, mergePersonalBests } from './personal-bests-io.js';
import { adoptSettings, onSettingsPersisted, settings } from './settings.svelte.js';
import { loadAllModeStats, mergeSoloStats } from './solo-stats-io.js';
import type { ModeStats } from './solo-stats-io.js';
import { pushSoloStats } from './stats-io.js';

export type AccountStatus =
  /** The probe has not answered yet. */
  | 'unknown'
  /** No account, by choice or because nobody has signed in on this device. */
  | 'guest'
  | 'signed-in'
  /** This deployment has no accounts, or cannot reach them. Guest play is unaffected. */
  | 'unavailable';

interface AccountState {
  status: AccountStatus;
  player: AccountPlayer | null;
  record: AccountRecord | null;
  bests: readonly AccountBest[];
  /**
   * Whether the server says this account is an administrator.
   *
   * It decides whether the profile sheet *offers* the admin panel, and that
   * is all it does. Every `/api/admin/*` route resolves the session and asks
   * again, server-side, so setting this by hand in a console buys a screen
   * full of 403s. `apps/server/src/http/admin-api.ts` is the gate; this is a
   * link.
   */
  isAdmin: boolean;
  /** A refusal worth showing on the account screen. Cleared by the next attempt. */
  error: string;
  busy: boolean;
}

export const account: AccountState = $state({
  status: 'unknown',
  player: null,
  record: null,
  bests: [],
  isAdmin: false,
  error: '',
  busy: false,
});

/**
 * How long a settings change waits before it is pushed.
 *
 * Long enough that clicking through the six theme swatches to see them is one
 * request rather than six, short enough that closing the tab straight after a
 * change almost always still catches it.
 */
const SETTINGS_PUSH_DELAY_MS = 800;
let settingsTimer: ReturnType<typeof setTimeout> | null = null;

/**
 * Ask the server who we are. Safe to call at any time; safe to fail.
 *
 * `signed-out` and `unavailable` are both ordinary answers rather than errors,
 * and `offline` is deliberately mapped to `unavailable` too: from a screen's
 * point of view "there is no server here" and "I cannot reach the server" are
 * the same situation and take the same message.
 */
export async function refresh(): Promise<void> {
  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    account.status = 'unavailable';
    return;
  }
  const result = await fetchProfile();
  applyProfile(result);
}

function applyProfile(result: Awaited<ReturnType<typeof fetchProfile>>): void {
  if (result.status === 'ok') {
    account.status = 'signed-in';
    account.player = result.value.player;
    account.record = result.value.record;
    account.bests = result.value.bests;
    account.isAdmin = result.value.isAdmin === true;
    mergePersonalBests(result.value.bests);
    // The rounds played on one device, synced on every load that finds a
    // session, so a round played on another device shows here too. Its own
    // route and its own store: nothing of it reaches the bests above. Bound
    // first: only this account's rounds (and the guest's, now its) go up.
    bindLocalHistory(result.value.player.id);
    void syncLocalHistory();
    return;
  }
  // The server says nobody is signed in (a session that expired, or ended on
  // another device): the guest's rounds show from now on, and the account's
  // stay parked on the device for its next sign-in, never sent anywhere else.
  // An unreachable server says nothing about who is signed in, so it changes
  // nothing.
  if (result.status === 'signed-out') releaseLocalHistory(false);
  account.player = null;
  account.record = null;
  account.bests = [];
  account.isAdmin = false;
  account.status = result.status === 'signed-out' ? 'guest' : 'unavailable';
}

export async function register(username: string, password: string): Promise<boolean> {
  return authenticate(() => registerRequest(username, password));
}

export async function signIn(username: string, password: string): Promise<boolean> {
  return authenticate(() => loginRequest(username, password));
}

async function authenticate(
  attempt: () => Promise<AccountOutcome<{ player: AccountPlayer }>>,
): Promise<boolean> {
  account.busy = true;
  account.error = '';
  try {
    const result = await attempt();
    if (result.status !== 'ok') {
      account.error = messageFor(result);
      if (result.status === 'unavailable') account.status = 'unavailable';
      return false;
    }
    account.status = 'signed-in';
    account.player = result.value.player;
    // Before anything else: a round that ends from here on is this account's.
    bindLocalHistory(result.value.player.id);
    await syncAfterSignIn();
    return true;
  } finally {
    account.busy = false;
  }
}

/**
 * The three syncs, in the order the amendment's sentence puts them.
 *
 * Bests go up first, because the merge is what decides them and the profile
 * read after it has to see the result. Settings come *down* if the account has
 * any and go *up* if it does not — a first sign-in on a device you have been
 * playing on should keep that device's setup, and a second device should
 * inherit it rather than overwrite it.
 */
async function syncAfterSignIn(): Promise<void> {
  const local = loadAllPersonalBests();
  const merged = await pushBests(local);
  if (merged.status === 'ok') {
    account.bests = merged.value.bests;
    mergePersonalBests(merged.value.bests);
  }

  // Solo stats go up the identical way bests do: this device's own lifetime
  // totals, merged server-side (elementwise max — `apps/server/src/accounts/
  // model.ts#betterSoloStats`) and the merged set written back down, so a
  // second device signing in later inherits totals it never personally
  // played.
  const localStats = loadAllModeStats();
  const mergedStats = await pushSoloStats(localStats);
  if (mergedStats.status === 'ok') {
    mergeSoloStats(mergedStats.value.solo.map(({ modeId, ...stats }) => ({ modeId, ...stats })));
  }

  const profile = await fetchProfile();
  if (profile.status !== 'ok') return;
  account.record = profile.value.record;
  account.bests = profile.value.bests;
  // The sign-in response says who you are; only `/me` says what you may do.
  account.isAdmin = profile.value.isAdmin === true;
  mergePersonalBests(profile.value.bests);

  if (profile.value.settings === null || profile.value.settings === undefined) {
    await pushSettings({ ...settings });
  } else {
    adoptSettings(profile.value.settings);
  }

  // The rounds played together on this device (`docs/decisions/0059`): up and
  // back down, a union, kept apart from the bests and the stats above. Only
  // this account's rounds go up, never another player's on a shared device.
  await syncLocalHistory();
}

export async function signOut(): Promise<void> {
  account.busy = true;
  // The account's rounds on one device go up once more while the session is
  // still good, so the server holds every one of them before they leave the
  // device (`docs/decisions/0059`, "After review").
  let roundsSynced = false;
  try {
    if (account.status === 'signed-in') roundsSynced = await syncLocalHistory();
    await logoutRequest();
  } finally {
    account.busy = false;
  }
  // The account's rounds leave the device when the server has them, and are
  // parked for its next sign-in here when it could not be reached: the next
  // person to sign in on this device never sees or uploads them.
  releaseLocalHistory(roundsSynced);
  // Local bests and settings stay exactly where they are: signing out makes
  // you a guest, and a guest keeps their localStorage.
  account.status = 'guest';
  account.player = null;
  account.record = null;
  account.bests = [];
  account.isAdmin = false;
  account.error = '';
}

/**
 * Record a finished run against the account too, if there is one.
 *
 * Fire and forget from the caller's point of view — the results screen has
 * already shown the local best and must not wait for a round trip to do it.
 */
/**
 * Change the signed-in player's password. Answers `null` when it worked, and
 * otherwise the sentence to show. Deliberately not written to `account.error`:
 * that belongs to the sign-in form, and this is asked from a sheet of its own.
 */
export async function changePassword(current: string, next: string): Promise<string | null> {
  const result = await changePasswordRequest(current, next);
  switch (result.status) {
    case 'ok':
      return null;
    case 'forbidden':
      return 'That is not your current password.';
    case 'signed-out':
      // The session ended somewhere else while the sheet was open.
      void refresh();
      return 'You are signed out. Sign in again to change your password.';
    case 'refused':
      return result.message === 'too many attempts'
        ? 'Too many attempts. Wait a minute and try again.'
        : result.message;
    case 'unavailable':
      return 'Accounts are unavailable right now. Try again in a moment.';
    case 'offline':
      return 'Could not reach the server. Try again when you are online.';
  }
}

export function syncBest(modeKey: string, record: unknown): void {
  if (account.status !== 'signed-in') return;
  void pushBests({ [modeKey]: record }).then((result) => {
    if (result.status === 'ok') account.bests = result.value.bests;
  });
}

/**
 * Push one mode's freshly-updated totals up, if there is an account.
 * Fire-and-forget from the caller's point of view, the identical shape
 * `syncBest` already has and for the identical reason: the local store has
 * already answered by the time this is called, and nothing on the results
 * screen should wait on a round trip to show it.
 */
export function syncStats(modeId: string, stats: ModeStats): void {
  if (account.status !== 'signed-in') return;
  void pushSoloStats({ [modeId]: stats });
}

/**
 * A socket ticket for the duel's join options, or `null`.
 *
 * `null` for a guest, for an unreachable server, for everything — the caller
 * joins the room either way. See `account-io.ts#fetchSocketTicket`.
 */
export async function socketTicket(): Promise<string | null> {
  if (account.status !== 'signed-in') return null;
  return fetchSocketTicket();
}

/**
 * Start listening for settings changes.
 *
 * Called once, from `App.svelte`, rather than at module load: a subscription
 * created as a side effect of an import is a subscription that exists in every
 * test that imports anything from this file.
 */
export function watchSettings(): () => void {
  return onSettingsPersisted((current) => {
    if (account.status !== 'signed-in') return;
    if (settingsTimer !== null) clearTimeout(settingsTimer);
    const snapshot = { ...current };
    settingsTimer = setTimeout(() => {
      settingsTimer = null;
      void pushSettings(snapshot);
    }, SETTINGS_PUSH_DELAY_MS);
  });
}

function messageFor(result: AccountOutcome<unknown>): string {
  switch (result.status) {
    case 'refused':
      return result.message;
    case 'unavailable':
      return 'Accounts are unavailable on this server. You can still play as a guest.';
    case 'offline':
      return 'Could not reach the server. You can still play as a guest.';
    case 'signed-out':
      return 'That username and password do not match.';
    default:
      return 'That did not work. Try again.';
  }
}
